import { c as _c } from "react/compiler-runtime";
import { IcoChevronLeftBig16, IcoChevronRightBig16, IcoMaximize16 } from '@onefootprint/icons';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { IconButton, LinkButton, LoadingSpinner, Tooltip } from '@onefootprint/ui';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENT_ONBOARDING_ID } from '../../../onboardings-shared/constants';
import { CURRENT_TAB_ID } from '../../constants';
import useOpenTrace from '../../hooks/use-open-trace';
import type { IntentHandlers } from '../../hooks/use-prefetch-onboardings';
import CurrentSummary from '../current-summary';
import OnboardingSummary from '../onboarding-summary';
import OnboardingStripItem from './components/onboarding-strip-item';
type OnboardingStripProps = {
  onboardings: EntityOnboarding[];
  selectedId: string;
  onSelect: (id: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  isLoadMoreError?: boolean;
  getIntentHandlers?: (onboarding: EntityOnboarding) => IntentHandlers;
};

// How much of each overflowing end fades out, under the arrow that scrolls it.
const FADE = '3rem';
const getScrollBehavior = (): ScrollBehavior => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
const OnboardingStrip = t0 => {
  const $ = _c(91);
  const {
    onboardings,
    selectedId,
    onSelect,
    onLoadMore,
    hasMore,
    isLoadingMore,
    isLoadMoreError,
    getIntentHandlers
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const openTrace = useOpenTrace();
  const stripRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const itemRefs = useRef(t2);
  const loadMoreRef = useRef(null);
  const nextPageRef = useRef(null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      start: false,
      end: false
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [overflow, setOverflow] = useState(t3);
  let t4;
  if ($[3] !== onboardings) {
    t4 = [CURRENT_ONBOARDING_ID, ...onboardings.map(_temp)];
    $[3] = onboardings;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const ids = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const strip = stripRef.current;
      if (!strip) {
        return;
      }
      const start = strip.scrollLeft > 0;
      const end = strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1;
      setOverflow(prev => prev.start === start && prev.end === end ? prev : {
        start,
        end
      });
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const checkOverflow = t5;
  let t6;
  if ($[6] !== hasMore || $[7] !== isLoadingMore || $[8] !== onboardings.length) {
    t6 = [onboardings.length, hasMore, isLoadingMore];
    $[6] = hasMore;
    $[7] = isLoadingMore;
    $[8] = onboardings.length;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(checkOverflow, t6);
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const strip_0 = stripRef.current;
      if (!strip_0) {
        return;
      }
      const observer = new ResizeObserver(checkOverflow);
      observer.observe(strip_0);
      return () => observer.disconnect();
    };
    t8 = [];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = item => {
      const strip_1 = stripRef.current;
      if (!strip_1) {
        return;
      }
      const isVisible = item.offsetLeft >= strip_1.scrollLeft && item.offsetLeft + item.offsetWidth <= strip_1.scrollLeft + strip_1.clientWidth;
      if (!isVisible) {
        strip_1.scrollLeft = item.offsetLeft - (strip_1.clientWidth - item.offsetWidth) / 2;
      }
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const reveal = t9;
  let t10;
  if ($[13] !== isLoadingMore || $[14] !== onLoadMore || $[15] !== onboardings.length) {
    t10 = () => {
      if (isLoadingMore) {
        return;
      }
      nextPageRef.current = {
        index: onboardings.length,
        hadFocus: Boolean(loadMoreRef.current?.contains(document.activeElement))
      };
      onLoadMore();
    };
    $[13] = isLoadingMore;
    $[14] = onLoadMore;
    $[15] = onboardings.length;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const loadMore = t10;
  let t11;
  if ($[17] !== onboardings) {
    t11 = () => {
      const nextPage = nextPageRef.current;
      if (!nextPage || onboardings.length <= nextPage.index) {
        return;
      }
      nextPageRef.current = null;
      const active = document.activeElement;
      const isFocusOnButton = !active || active === document.body || loadMoreRef.current?.contains(active);
      const item_0 = itemRefs.current.get(onboardings[nextPage.index].id);
      if (nextPage.hadFocus && isFocusOnButton && item_0) {
        item_0.focus({
          preventScroll: true
        });
        reveal(item_0);
      }
    };
    $[17] = onboardings;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== onboardings.length) {
    t12 = [onboardings.length];
    $[19] = onboardings.length;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[21] !== selectedId) {
    t13 = () => {
      const item_1 = itemRefs.current.get(selectedId);
      if (item_1) {
        reveal(item_1);
      }
    };
    t14 = [selectedId];
    $[21] = selectedId;
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  useEffect(t13, t14);
  const maskImage = overflow.start || overflow.end ? `linear-gradient(to right, ${overflow.start ? "transparent" : "#000"}, #000 ${FADE}, #000 calc(100% - ${FADE}), ${overflow.end ? "transparent" : "#000"})` : undefined;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = direction => {
      const strip_2 = stripRef.current;
      strip_2?.scrollBy({
        left: direction * strip_2.clientWidth * 0.8,
        behavior: getScrollBehavior()
      });
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const scrollByPage = t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  if ($[25] !== getIntentHandlers || $[26] !== ids || $[27] !== maskImage || $[28] !== onSelect || $[29] !== onboardings || $[30] !== openTrace || $[31] !== selectedId || $[32] !== t) {
    const setItemRef = id_0 => node => {
      if (node) {
        itemRefs.current.set(id_0, node);
      } else {
        itemRefs.current.delete(id_0);
      }
    };
    let t28;
    if ($[45] !== ids) {
      t28 = (event, index) => {
        const targetIndex = {
          ArrowRight: Math.min(index + 1, ids.length - 1),
          ArrowLeft: Math.max(index - 1, 0),
          Home: 0,
          End: ids.length - 1
        }[event.key];
        if (targetIndex === undefined) {
          return;
        }
        event.preventDefault();
        itemRefs.current.get(ids[targetIndex])?.focus();
      };
      $[45] = ids;
      $[46] = t28;
    } else {
      t28 = $[46];
    }
    const handleKeyDown = t28;
    t27 = "relative";
    t23 = stripRef;
    t24 = "relative flex items-stretch gap-1 overflow-x-auto p-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
    if ($[47] !== maskImage) {
      t25 = {
        maskImage,
        WebkitMaskImage: maskImage
      };
      $[47] = maskImage;
      $[48] = t25;
    } else {
      t25 = $[48];
    }
    t26 = checkOverflow;
    t16 = "tablist";
    if ($[49] !== t) {
      t17 = t(_temp2);
      $[49] = t;
      $[50] = t17;
    } else {
      t17 = $[50];
    }
    t18 = "horizontal";
    t19 = "flex gap-1";
    let t29;
    if ($[51] !== onSelect) {
      t29 = () => onSelect(CURRENT_ONBOARDING_ID);
      $[51] = onSelect;
      $[52] = t29;
    } else {
      t29 = $[52];
    }
    let t30;
    if ($[53] !== handleKeyDown) {
      t30 = event_0 => handleKeyDown(event_0, 0);
      $[53] = handleKeyDown;
      $[54] = t30;
    } else {
      t30 = $[54];
    }
    let t31;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <CurrentSummary />;
      $[55] = t31;
    } else {
      t31 = $[55];
    }
    const t32 = <OnboardingStripItem ref={setItemRef(CURRENT_ONBOARDING_ID)} id={CURRENT_TAB_ID} isSelected={selectedId === CURRENT_ONBOARDING_ID} onClick={t29} onKeyDown={t30}>{t31}</OnboardingStripItem>;
    if ($[56] !== t32) {
      t20 = <div className="w-55 shrink-0">{t32}</div>;
      $[56] = t32;
      $[57] = t20;
    } else {
      t20 = $[57];
    }
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <div aria-hidden={true} className="my-2 shrink-0 border-l border-solid border-tertiary" />;
      $[58] = t21;
    } else {
      t21 = $[58];
    }
    t22 = onboardings.map((onboarding, index_0) => {
      const isSelected = onboarding.id === selectedId;
      const hasTraceShortcut = isSelected && onboarding.hasGraphTrace;
      return <div key={onboarding.id} className="grid w-55 shrink-0 grid-cols-1 [&>*]:[grid-area:1/1]"><OnboardingStripItem ref={setItemRef(onboarding.id)} id={onboarding.id} isSelected={isSelected} intentHandlers={isSelected ? undefined : getIntentHandlers?.(onboarding)} onClick={() => onSelect(onboarding.id)} onKeyDown={event_1 => handleKeyDown(event_1, index_0 + 1)}><OnboardingSummary onboarding={onboarding} hasAction={hasTraceShortcut} /></OnboardingStripItem>{hasTraceShortcut && <div aria-hidden={true} className="mt-1.5 mr-1.25 self-start justify-self-end"><Tooltip content={t(_temp3)}><IconButton aria-label={t(_temp4)} icon={IcoMaximize16} size="tiny" className="[&_svg]:size-3 [&_svg]:[--central-icon-stroke:1.125px]!" tabIndex={-1} onClick={event_2 => openTrace(event_2, onboarding.id)} /></Tooltip></div>}</div>;
    });
    $[25] = getIntentHandlers;
    $[26] = ids;
    $[27] = maskImage;
    $[28] = onSelect;
    $[29] = onboardings;
    $[30] = openTrace;
    $[31] = selectedId;
    $[32] = t;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
    $[41] = t24;
    $[42] = t25;
    $[43] = t26;
    $[44] = t27;
  } else {
    t16 = $[33];
    t17 = $[34];
    t18 = $[35];
    t19 = $[36];
    t20 = $[37];
    t21 = $[38];
    t22 = $[39];
    t23 = $[40];
    t24 = $[41];
    t25 = $[42];
    t26 = $[43];
    t27 = $[44];
  }
  let t28;
  if ($[59] !== t16 || $[60] !== t17 || $[61] !== t18 || $[62] !== t19 || $[63] !== t20 || $[64] !== t21 || $[65] !== t22) {
    t28 = <div role={t16} aria-label={t17} aria-orientation={t18} className={t19}>{t20}{t21}{t22}</div>;
    $[59] = t16;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
    $[65] = t22;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== hasMore || $[68] !== isLoadMoreError || $[69] !== isLoadingMore || $[70] !== loadMore || $[71] !== t) {
    t29 = hasMore && <div ref={loadMoreRef} className="flex shrink-0 items-center gap-x-2 px-3 text-body-3 whitespace-nowrap text-tertiary">{isLoadMoreError && !isLoadingMore && t(_temp5)}<LinkButton variant="label-3" aria-disabled={isLoadingMore || undefined} onClick={loadMore}>{isLoadMoreError && !isLoadingMore ? t(_temp6) : t(_temp7)}</LinkButton>{isLoadingMore && <LoadingSpinner size="compact" />}</div>;
    $[67] = hasMore;
    $[68] = isLoadMoreError;
    $[69] = isLoadingMore;
    $[70] = loadMore;
    $[71] = t;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== t23 || $[74] !== t24 || $[75] !== t25 || $[76] !== t26 || $[77] !== t28 || $[78] !== t29) {
    t30 = <div ref={t23} className={t24} style={t25} onScroll={t26}>{t28}{t29}</div>;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== overflow.start || $[81] !== t) {
    t31 = overflow.start && <div className="absolute inset-y-0 left-1 flex items-center pointer-coarse:hidden"><IconButton aria-label={t(_temp8)} icon={IcoChevronLeftBig16} variant="outline" size="compact" tabIndex={-1} onClick={() => scrollByPage(-1)} /></div>;
    $[80] = overflow.start;
    $[81] = t;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  let t32;
  if ($[83] !== overflow.end || $[84] !== t) {
    t32 = overflow.end && <div className="absolute inset-y-0 right-1 flex items-center pointer-coarse:hidden"><IconButton aria-label={t(_temp9)} icon={IcoChevronRightBig16} variant="outline" size="compact" tabIndex={-1} onClick={() => scrollByPage(1)} /></div>;
    $[83] = overflow.end;
    $[84] = t;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  let t33;
  if ($[86] !== t27 || $[87] !== t30 || $[88] !== t31 || $[89] !== t32) {
    t33 = <div className={t27}>{t30}{t31}{t32}</div>;
    $[86] = t27;
    $[87] = t30;
    $[88] = t31;
    $[89] = t32;
    $[90] = t33;
  } else {
    t33 = $[90];
  }
  return t33;
};
export default OnboardingStrip;
function _temp(t0) {
  const {
    id
  } = t0;
  return id;
}
function _temp2($) {
  return $.title;
}
function _temp3($_0) {
  return $_0.header.openTrace;
}
function _temp4($_1) {
  return $_1.header.openTrace;
}
function _temp5($_2) {
  return $_2.list.loadOlderFailed;
}
function _temp6($_3) {
  return $_3.list.tryAgain;
}
function _temp7($_4) {
  return $_4.list.showOlder;
}
function _temp8($_5) {
  return $_5.list.scrollNewer;
}
function _temp9($_6) {
  return $_6.list.scrollOlder;
}